import React, { useCallback, useState } from 'react';

import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, Box, Button } from '@mui/material';

import { SecretDisplay as SecretDisplayBase } from './SecretDisplay/SecretDisplay';
import TimeoutTimer from './TimeoutTimer';

import type { DecryptedSecret } from '@/types';

interface SecretDisplayWithTimeoutProps {
    secret: DecryptedSecret;
    /** Timeout in seconds (from API response) */
    timeout?: number | string;
    onDownloadFile?: () => Promise<void>;
    onDecryptFile?: () => Promise<ArrayBuffer | null>;
    /** Callback when timeout expires */
    onTimeoutExpire?: () => void;
}

/**
 * SecretDisplay with optional timeout countdown
 * Wraps the base SecretDisplay with a timer that hides content when expired
 */
export const SecretDisplayWithTimeout: React.FC<SecretDisplayWithTimeoutProps> = ({
    secret,
    timeout,
    onDownloadFile,
    onDecryptFile,
    onTimeoutExpire,
}) => {
    const [isExpired, setIsExpired] = useState(false);

    // Parse timeout - could be string "60" or number 60
    const timeoutSeconds = timeout ? (typeof timeout === 'string' ? parseInt(timeout, 10) : timeout) : null;

    const handleExpire = useCallback(() => {
        setIsExpired(true);
        onTimeoutExpire?.();
    }, [onTimeoutExpire]);

    // If expired, show expired message
    if (isExpired) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <Alert severity="warning" sx={{ mb: 3 }}>
                    The viewing time has expired. The secret has been hidden for your security.
                </Alert>

                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => window.location.reload()}>
                    Reload to view again
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            {/* Show timer only if timeout is set */}
            {timeoutSeconds && timeoutSeconds > 0 && <TimeoutTimer timeout={timeoutSeconds} onExpire={handleExpire} />}

            {/* The actual secret content */}
            <SecretDisplayBase secret={secret} onDownloadFile={onDownloadFile} onDecryptFile={onDecryptFile} />
        </Box>
    );
};

export default SecretDisplayWithTimeout;
